import React from "react"
import { graphql } from "gatsby"
import { withTheme } from "emotion-theming"

import Layout from "@layouts/base"

import Circle from "@components/circle"
import { Container, Row, Col } from "@components/grid"
import {
  Collapse,
  CollapseItem,
  CollapseHeading,
  CollapseButton,
  CollapsePanel,
} from "@components/collapse"
import Infographic from "@components/infographic"
import { Number, NumberText, NumberRow } from "@components/number"
import Panel from "@components/panel"
import Player from "@components/player"
import { Table, Thead, Tbody, Tr, Th, Td } from "@components/table"
import Testimonial from "@components/testimonial"
import {
  BlockQuote,
  Caption,
  Cite,
  Heading,
  Pullquote,
  Strong,
} from "@components/type"

import Check from "@svgs/check"

import { mathStats } from "@data"

import { People } from "@svgs"
import deban from "@audio/deban.m4a"

const Family = ({ theme, data }) => {
  const pageTitle = "Education Department"
  return (
    <Layout image={data.hero.childImageSharp.fluid} title={pageTitle}>
      <Container>
        <Heading colour={theme.colours.primary}>{pageTitle}</Heading>
        <Player
          title="Deban & Shane – Education Department Podcast"
          src={deban}
        />
        <h2>Jack Parent Reading Program</h2>
        <h3>Goals of the Reading Tutor Program</h3>
        <NumberRow>
          <Number background={theme.colours.lightPrimary}>1</Number>
          <NumberText>
            Improve the reading skills of children in Grades 1-3 who are reading
            below grade level and who are under-resourced
          </NumberText>
        </NumberRow>
        <NumberRow>
          <Number background={theme.colours.secondary}>2</Number>
          <NumberText>
            Increase a child's self-confidence through one-on-one support in
            comprehension and decoding skills
          </NumberText>
        </NumberRow>
        <NumberRow>
          <Number background={theme.colours.success}>3</Number>
          <NumberText>
            Foster a love of reading, an appreciation of literature and provide
            positive role models
          </NumberText>
        </NumberRow>
        <h3>Reading Program Outcomes</h3>
        <Row>
          <Col width={1 / 2}>
            <Infographic
              icon={<People />}
              value="225"
              colour={theme.colours.secondary}
            >
              students reported data for June 2018
            </Infographic>
          </Col>
          <Col width={1 / 2}>
            <Infographic
              icon={<Circle value={83} text="83%" />}
              lg={true}
              colour={theme.colours.primary}
            >
              of the students stated that they liked reading with their tutor
            </Infographic>
          </Col>
        </Row>
        <Row>
          <Col width={1 / 2}>
            <Infographic
              icon={<Circle value={90} text="90%" />}
              lg={true}
              colour={theme.colours.danger}
            >
              of teachers and parents reported an increase in their student’s
              self-confidence
            </Infographic>
          </Col>
          <Col width={1 / 2}>
            <Infographic
              icon={<Circle value={94} text="94%" />}
              lg={true}
              colour={theme.colours.success}
            >
              of the students showed improvement in their reading skills
            </Infographic>
          </Col>
        </Row>
      </Container>
      <Panel background={theme.colours.warning}>
        <h2>Since 2003, we have...</h2>
        <Caption>
          <Check /> helped a total of <Strong>2798</Strong> students over{" "}
          <Strong>15 years</Strong>
        </Caption>
        <Caption>
          <Check /> grown to include <Strong>14 schools</Strong> in our program
        </Caption>
        <Caption>
          <Check /> grown from <Strong>15</Strong> (2004) to{" "}
          <Strong>231</Strong> (2018) students in program
        </Caption>
        {/* <Table>
          <Thead>
            <Tr background={theme.colours.secondary}>
              {readingStats.cols.map(label => (
                <Th key={label}>{label}</Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {readingStats.rows.map((row, i) => (
              <Tr key={row.Statistics} background={theme.backgroundColor}>
                {readingStats.cols.map(label => (
                  <Td key={label} data-label={label}>
                    {row[label]}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table> */}
      </Panel>
      <Panel>
        <h2>Jack Parent Reading Program</h2>
        <Collapse allowZeroExpanded={true}>
          <CollapseItem>
            <CollapseHeading>
              <CollapseButton background={theme.colours.primary}>
                Student
              </CollapseButton>
            </CollapseHeading>
            <CollapsePanel nopad="true">
              <Testimonial
                background={theme.colours.primary}
                image={data.student.childImageSharp.fixed}
                nospace={true}
              >
                <BlockQuote>
                  My reading tutors see how much I read and I’m proud of myself.
                </BlockQuote>
                <Cite>- Student</Cite>
              </Testimonial>
            </CollapsePanel>
          </CollapseItem>
          <CollapseItem>
            <CollapseHeading>
              <CollapseButton background={theme.colours.primary}>
                Parent
              </CollapseButton>
            </CollapseHeading>
            <CollapsePanel nopad="true">
              <Testimonial
                background={theme.colours.primary}
                image={data.parent.childImageSharp.fixed}
                nospace={true}
              >
                <BlockQuote>
                  My child is more confident when reading! They no longer
                  hesitate when seeing a word they may not know how to read,
                  instead they pronounce each syllable or ask for help. They
                  have definitely improved in reading since participating in the
                  N2N reading program!
                </BlockQuote>
                <Cite>- Parent</Cite>
              </Testimonial>
            </CollapsePanel>
          </CollapseItem>
          <CollapseItem>
            <CollapseHeading>
              <CollapseButton background={theme.colours.primary}>
                Teacher
              </CollapseButton>
            </CollapseHeading>
            <CollapsePanel nopad="true">
              <Testimonial
                background={theme.colours.primary}
                image={data.teacher.childImageSharp.fixed}
                nospace={true}
              >
                <BlockQuote>
                  It’s always such a joy to see children’s reading skills,
                  overall skills and confidence soar! Thank you for your
                  continued support of our young learners!
                </BlockQuote>
                <Cite>- Teacher</Cite>
              </Testimonial>
            </CollapsePanel>
          </CollapseItem>
          <CollapseItem>
            <CollapseHeading>
              <CollapseButton background={theme.colours.primary}>
                Parent
              </CollapseButton>
            </CollapseHeading>
            <CollapsePanel nopad="true">
              <Testimonial
                background={theme.colours.primary}
                image={data.tutor.childImageSharp.fixed}
                nospace={true}
              >
                <BlockQuote>
                  The students made connections to their own lives through the
                  books they read showing comprehension and an appreciation of
                  reading, but also growing confidence in expressing their
                  opinion.
                </BlockQuote>
                <Cite>- Tutor</Cite>
              </Testimonial>
            </CollapsePanel>
          </CollapseItem>
        </Collapse>

        <Pullquote colour={theme.colours.primary}>
          $7,500 provides the funds to support an entire school program for one
          year.
        </Pullquote>
        <h2>Math Program</h2>
        <h3>Goals of the Math Success Program:</h3>
        <NumberRow>
          <Number background={theme.colours.lightPrimary}>1</Number>
          <NumberText>
            Improve students math skills, build proficiency and self-confidence
          </NumberText>
        </NumberRow>
        <NumberRow>
          <Number background={theme.colours.secondary}>2</Number>
          <NumberText>
            Acquire knowledge to support students successful transition into
            high school
          </NumberText>
        </NumberRow>
        <NumberRow>
          <Number background={theme.colours.success}>3</Number>
          <NumberText>
            Build relationships to help create “social capital” or “support
            systems”
          </NumberText>
        </NumberRow>
        <NumberRow>
          <Number background={theme.colours.danger}>4</Number>
          <NumberText>
            Take part in mentorship and counselling that will help participants
            set goals and build aspirations for potential career paths
          </NumberText>
        </NumberRow>
        <h3>Math Program Outcomes:</h3>
        <Row>
          <Col width={1 / 2}>
            <Infographic
              icon={<People />}
              value="24/25"
              colour={theme.colours.secondary}
            >
              students reported assessment information over the course of the
              2018 program.
            </Infographic>
          </Col>
          <Col width={1 / 2}>
            <Infographic
              icon={<Circle value={66} text="66%" />}
              lg={true}
              colour={theme.colours.success}
            >
              of tutors reported that they built relationships with students
              over the length of the program.
            </Infographic>
          </Col>
        </Row>
        <Row>
          <Col width={1 / 2}>
            <Infographic
              icon={<Circle value={42} text="42%" />}
              lg={true}
              colour={theme.colours.secondary}
            >
              Over the duration of the program there was a 42% average increase
              in students' math skills.
            </Infographic>
          </Col>
          <Col width={1 / 2}>
            <Infographic
              icon={<Circle value={89} text="89%" />}
              lg={true}
              colour={theme.colours.success}
            >
              of the students indicated feeling confident in their math skills
              at the end of the program.
            </Infographic>
          </Col>
        </Row>
      </Panel>
      <Panel background={theme.color}>
        <Table>
          <Thead>
            <Tr background={theme.colours.success}>
              {mathStats.cols.map(label => (
                <Th key={label}>{label}</Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {mathStats.rows.map(row => (
              <Tr key={row.Statistics} background={theme.backgroundColor}>
                {mathStats.cols.map(label => (
                  <Td key={label} data-label={label}>
                    {row[label]}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Panel>
      <Panel>
        <h2>Math Program Feedback:</h2>
        <Collapse allowZeroExpanded={true}>
          <CollapseItem>
            <CollapseHeading>
              <CollapseButton background={theme.colours.primary}>
                Student
              </CollapseButton>
            </CollapseHeading>
            <CollapsePanel nopad="true">
              <Testimonial
                background={theme.colours.primary}
                image={data.studentTwo.childImageSharp.fixed}
                nospace={true}
              >
                <BlockQuote>
                  I got way better grades. Last year they were very very bad but
                  I have tried a lot harder and I improved a lot.
                </BlockQuote>
                <Cite>- Student</Cite>
              </Testimonial>
            </CollapsePanel>
          </CollapseItem>
          <CollapseItem>
            <CollapseHeading>
              <CollapseButton background={theme.colours.primary}>
                Tutor
              </CollapseButton>
            </CollapseHeading>
            <CollapsePanel nopad="true">
              <Testimonial
                background={theme.colours.primary}
                image={data.tutorTwo.childImageSharp.fixed}
                nospace={true}
              >
                <BlockQuote>
                  I found the most memorable moment for me was when I was able
                  to explain to a student how to perform long division. Hearing
                  the words, "I understand it now!" was definitely gratifying
                  for me as a tutor.
                </BlockQuote>
                <Cite>- Tutor</Cite>
              </Testimonial>
            </CollapsePanel>
          </CollapseItem>
          <CollapseItem>
            <CollapseHeading>
              <CollapseButton background={theme.colours.primary}>
                Parent
              </CollapseButton>
            </CollapseHeading>
            <CollapsePanel nopad="true">
              <Testimonial
                background={theme.colours.primary}
                image={data.parentTwo.childImageSharp.fixed}
                nospace={true}
              >
                <BlockQuote>
                  This has been a positive experience and we very much
                  appreciate our child being given the opportunity. Their
                  confidence has increased greatly.
                </BlockQuote>
                <Cite>- Parent</Cite>
              </Testimonial>
            </CollapsePanel>
          </CollapseItem>
        </Collapse>
      </Panel>
    </Layout>
  )
}

export const query = graphql`
  query {
    hero: file(relativePath: { eq: "hero/education.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1920, quality: 100) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    student: file(relativePath: { eq: "education/student.jpg" }) {
      childImageSharp {
        fixed(width: 200, quality: 100) {
          ...GatsbyImageSharpFixed
        }
      }
    }
    studentTwo: file(relativePath: { eq: "education/student-2.jpg" }) {
      childImageSharp {
        fixed(width: 200, quality: 100) {
          ...GatsbyImageSharpFixed
        }
      }
    }
    parent: file(relativePath: { eq: "education/parent.jpg" }) {
      childImageSharp {
        fixed(width: 200, quality: 100) {
          ...GatsbyImageSharpFixed
        }
      }
    }
    parentTwo: file(relativePath: { eq: "education/parent-2.jpg" }) {
      childImageSharp {
        fixed(width: 200, quality: 100) {
          ...GatsbyImageSharpFixed
        }
      }
    }
    teacher: file(relativePath: { eq: "education/teacher.jpg" }) {
      childImageSharp {
        fixed(width: 200, quality: 100) {
          ...GatsbyImageSharpFixed
        }
      }
    }
    tutor: file(relativePath: { eq: "education/tutor.jpg" }) {
      childImageSharp {
        fixed(width: 200, quality: 100) {
          ...GatsbyImageSharpFixed
        }
      }
    }
    tutorTwo: file(relativePath: { eq: "education/tutor-2.jpg" }) {
      childImageSharp {
        fixed(width: 200, quality: 100) {
          ...GatsbyImageSharpFixed
        }
      }
    }
  }
`

export default withTheme(Family)
